/**
 * ContiList — Pagina lista conti bancari collegati.
 *
 * Mostra una griglia di SaldoCard per ogni conto del tenant,
 * un riepilogo della liquidità aggregata e il bottone per collegare
 * un nuovo conto tramite Tink Link (flusso PSD2).
 *
 * Gestisce il callback Tink: se il query param "code" è presente nell'URL,
 * chiama l'endpoint di callback e poi ricarica la lista.
 *
 * Importa esclusivamente da @floraltech/ui (regola 6.8 CLAUDE.md).
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Landmark, Plus, RefreshCw } from 'lucide-react';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@floraltech/ui';
import { useFormatters } from '@lib/formatters';
import { SaldoCard } from '../components/SaldoCard';

// ---------------------------------------------------------------------------
// Interfaces
// ---------------------------------------------------------------------------

/** Dati di un conto bancario collegato restituiti dall'API. */
interface ContoItem {
  id: string;
  nome: string;
  iban: string;
  saldo_contabile: number | null;
  saldo_disponibile: number | null;
  valuta: string;
  stato_connessione: string;
  ultimo_sync: string | null;
  consenso_scadenza: string | null;
}

/** Risposta dell'endpoint /liquidita. */
interface LiquiditaData {
  totale_contabile: number;
  totale_disponibile: number;
}

// ---------------------------------------------------------------------------
// Auth helper
// ---------------------------------------------------------------------------

/**
 * Restituisce gli header di autenticazione Bearer per le richieste API.
 *
 * @returns Record con header Authorization.
 */
const authGet = (): Record<string, string> => ({
  Authorization: `Bearer ${localStorage.getItem('token') ?? ''}`,
});

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Pagina principale lista conti bancari.
 *
 * Al mount carica i conti e la liquidità aggregata in parallelo.
 * Se l'URL contiene il query param "code" (callback Tink), completa
 * il collegamento prima di ricaricare i dati.
 *
 * @returns Pagina con griglia SaldoCard e riepilogo liquidità.
 */
const ContiList: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const fmt = useFormatters();

  const [conti, setConti] = useState<ContoItem[]>([]);
  const [liquidita, setLiquidita] = useState<LiquiditaData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [collegando, setCollegando] = useState(false);
  const [callbackInProgress, setCallbackInProgress] = useState(false);

  /**
   * Carica la lista conti e la liquidità aggregata in parallelo.
   */
  const caricaDati = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [contiRes, liquiditaRes] = await Promise.all([
        fetch('/api/v1/banca/conti', { headers: authGet() }),
        fetch('/api/v1/banca/conti/liquidita', { headers: authGet() }),
      ]);

      if (!contiRes.ok) throw new Error(`Errore caricamento conti: ${contiRes.status}`);
      if (!liquiditaRes.ok)
        throw new Error(`Errore caricamento liquidità: ${liquiditaRes.status}`);

      const contiData: ContoItem[] = await contiRes.json();
      const liquiditaData: LiquiditaData = await liquiditaRes.json();

      setConti(contiData);
      setLiquidita(liquiditaData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore sconosciuto');
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Gestisce il callback Tink: scambia il code con i conti collegati,
   * poi rimuove il param dall'URL e ricarica i dati.
   *
   * @param code - Authorization code ricevuto da Tink Link.
   */
  const gestisciCallback = useCallback(
    async (code: string) => {
      setCallbackInProgress(true);
      setError(null);
      try {
        const res = await fetch(
          `/api/v1/banca/conti/callback?code=${encodeURIComponent(code)}`,
          { headers: authGet() },
        );
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(
            body?.detail ?? `Errore callback Tink: ${res.status}`,
          );
        }
        // Rimuove il query param dall'URL senza aggiungere voce alla history
        setSearchParams({}, { replace: true });
        await caricaDati();
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Errore durante il collegamento',
        );
      } finally {
        setCallbackInProgress(false);
      }
    },
    [caricaDati, setSearchParams],
  );

  // Al mount: gestisci callback se presente, altrimenti carica direttamente
  useEffect(() => {
    const code = searchParams.get('code');
    const tinkError = searchParams.get('error');
    const tinkErrorReason = searchParams.get('error_reason');
    const tinkMessage = searchParams.get('message');

    if (tinkError) {
      const msg = tinkMessage
        ? decodeURIComponent(tinkMessage)
        : `Errore Tink: ${tinkErrorReason ?? tinkError}`;
      const fullMsg = `${msg}${tinkErrorReason ? ` (${tinkErrorReason})` : ''}`;
      setSearchParams({}, { replace: true });
      // caricaDati() resetta setError(null) all'inizio: setto errore DOPO che termina
      caricaDati().then(() => setError(fullMsg));
    } else if (code) {
      gestisciCallback(code);
    } else {
      caricaDati();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Avvia il flusso Tink Link: chiama POST /collega e redirige l'utente.
   */
  const collegaConto = useCallback(async () => {
    setCollegando(true);
    setError(null);
    try {
      const res = await fetch('/api/v1/banca/conti/collega', {
        method: 'POST',
        headers: {
          ...authGet(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.detail ?? `Errore avvio collegamento: ${res.status}`);
      }
      const data: { url: string } = await res.json();
      // Redirect esterno verso Tink Link (fuori dall'app React)
      window.location.href = data.url;
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Impossibile avviare il collegamento',
      );
      setCollegando(false);
    }
  }, []);

  // ---------------------------------------------------------------------------
  // Render helpers
  // ---------------------------------------------------------------------------

  const isProcessing = loading || callbackInProgress;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Landmark className="h-5 w-5 text-muted-foreground" />
          <h1 className="text-2xl font-semibold font-display">Banca</h1>
        </div>
        <Button
          onClick={collegaConto}
          disabled={collegando || isProcessing}
          className="gap-2"
        >
          <Plus className="h-4 w-4" />
          Collega conto
        </Button>
      </div>

      {/* Feedback errore */}
      {error && (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {/* Callback in corso */}
      {callbackInProgress && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <RefreshCw className="h-4 w-4 animate-spin" />
          Collegamento in corso…
        </div>
      )}

      {/* Riepilogo liquidità */}
      {liquidita && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                Liquidità contabile totale
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-semibold tabular-nums">
                {fmt.currency(liquidita.totale_contabile)}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                Liquidità disponibile totale
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-semibold tabular-nums">
                {fmt.currency(liquidita.totale_disponibile)}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Griglia conti */}
      {isProcessing ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-8">
          <RefreshCw className="h-4 w-4 animate-spin" />
          Caricamento conti…
        </div>
      ) : conti.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center text-muted-foreground gap-3">
          <Landmark className="h-10 w-10 opacity-30" />
          <p className="text-sm">
            Nessun conto collegato. Collega il tuo primo conto per iniziare.
          </p>
          <Button
            variant="outline"
            onClick={collegaConto}
            disabled={collegando}
            className="mt-2 gap-2"
          >
            <Plus className="h-4 w-4" />
            Collega conto
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {conti.map((conto) => (
            <SaldoCard
              key={conto.id}
              id={conto.id}
              nome={conto.nome}
              iban={conto.iban}
              saldo_contabile={conto.saldo_contabile}
              saldo_disponibile={conto.saldo_disponibile}
              valuta={conto.valuta}
              stato_connessione={conto.stato_connessione}
              ultimo_sync={conto.ultimo_sync}
              consenso_scadenza={conto.consenso_scadenza}
              onClick={() => navigate(`/banca/conti/${conto.id}`)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ContiList;
